import React, {useMemo, useState} from 'react';
import {Modal, Select} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import type {MRT_ColumnDef, MRT_RowSelectionState} from 'mantine-react-table';
import type {CourseAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {DepartmentSelectBox} from 'src/app/databound-elements/DepartmentSelectBox';
import {useGetCourseAssignmentListV2Query} from 'src/redux/queries/courseAssignment';
import {useGetVendorSelectOptionsQuery} from 'src/redux/queries/vendor';
import type {ISelectOption} from 'src/types/Form';

export interface RosterBuilderModalProps {
  opened: boolean;
  onClose: () => void;
  courseDefinitionId: number;
  capacity?: number;
  currentRosterCount: number;
  existingAssignmentIds: number[];
  isSaving: boolean;
  onAdd: (assignmentIds: number[]) => void;
}

/**
 * Filtered roster builder — replaces the flat 10,000-row combobox from
 * EditCourseClassroom. Search + department/vendor filters over people
 * already assigned to this course; multi-add via checkbox selection with a
 * live capacity counter. Walk-ins who are not yet assigned the course are
 * out of scope this wave (assign them to the course first).
 */
export const RosterBuilderModal: React.FC<RosterBuilderModalProps> = ({
  opened,
  onClose,
  courseDefinitionId,
  capacity,
  currentRosterCount,
  existingAssignmentIds,
  isSaving,
  onAdd,
}) => {
  const [departmentId, setDepartmentId] = useState<string | undefined>(undefined);
  const [vendorId, setVendorId] = useState<string | undefined>(undefined);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const {data: vendorOptions} = useGetVendorSelectOptionsQuery({
    start: 0,
    length: 500,
    active: true,
  });

  const columns = useMemo<MRT_ColumnDef<CourseAssignmentSimpleResponse>[]>(
    () => [
      {accessorKey: 'assignedTo.name', header: 'Person'},
      {accessorKey: 'assignedTo.employeeId', header: 'Employee ID'},
      {accessorKey: 'assignedTo.department.description', header: 'Department'},
      {accessorKey: 'assignedTo.vendor.name', header: 'Vendor'},
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'training-roster-builder',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 10},
  });
  const {start, length, filters, filterFns, sorting, columnFilters} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetCourseAssignmentListV2Query(
      {
        start,
        length,
        filters,
        filterFns,
        sorting,
        courseDefinitionId,
        departmentId: departmentId ? Number(departmentId) : undefined,
        vendorId: vendorId ? Number(vendorId) : undefined,
      },
      {skip: !opened}
    );

  const existingSet = useMemo(
    () => new Set(existingAssignmentIds),
    [existingAssignmentIds]
  );
  const rows = (data?.data ?? []).filter(
    row => row.id !== undefined && !existingSet.has(row.id)
  );
  const totalCount = data?.totalRecords ?? 0;

  const selectedIds = Object.keys(rowSelection)
    .filter(id => rowSelection[id])
    .map(Number);
  const projectedCount = currentRosterCount + selectedIds.length;
  const overCapacity = capacity !== undefined && projectedCount > capacity;

  const handleClose = () => {
    setRowSelection({});
    setDepartmentId(undefined);
    setVendorId(undefined);
    onClose();
  };

  const handleAdd = () => {
    if (selectedIds.length === 0) {
      return;
    }
    onAdd(selectedIds);
    setRowSelection({});
  };

  const {table} = useTable({
    ...tableState,
    rowSelection,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowActions: false,
      enableRowSelection: true,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      onRowSelectionChange: setRowSelection,
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  const hasActiveTableFilter = columnFilters.length > 0;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add Attendees"
      size="1000px"
      centered>
      <Text size="sm" c="dimmed" mb="md">
        Only people already assigned to this course can join the class.
        Missing someone? Assign them to the course first, then add them here.
      </Text>

      <Group mb="md" align="flex-end">
        <Box style={{width: '220px'}}>
          <DepartmentSelectBox
            label="Department"
            queryOptions={{}}
            value={departmentId}
            placeholder="All departments"
            onSelectChangeCallback={(value: ISelectOption | null) =>
              setDepartmentId(value?.id ? String(value.id) : undefined)
            }
          />
        </Box>
        <Box style={{width: '220px'}}>
          <Select
            label="Vendor"
            placeholder="All vendors"
            data={(vendorOptions?.data ?? []).map(option => ({
              value: String(option.id),
              label: option.label ?? String(option.id),
            }))}
            value={vendorId ?? null}
            onChange={value => setVendorId(value ?? undefined)}
            searchable
            clearable
          />
        </Box>
        <Text size="sm" fw={500} c={overCapacity ? 'red' : undefined}>
          {capacity !== undefined
            ? `${projectedCount} / ${capacity}`
            : `${projectedCount} selected`}
        </Text>
      </Group>

      {overCapacity && (
        <Alert color="yellow" mb="md" title="Over capacity">
          This selection puts the class over its capacity of {capacity}.
          You can still add them, but consider scheduling another class.
        </Alert>
      )}

      {hasActiveTableFilter && (
        <Text size="xs" c="dimmed" mb="xs">
          Note: selection only tracks rows on the current page while a search
          filter is narrowing results.
        </Text>
      )}

      {table}

      <Group justify="end" mt="md">
        <Button variant="subtle" color="black" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          loading={isSaving}
          disabled={selectedIds.length === 0}>
          {`Add ${selectedIds.length || ''}`.trim()}
        </Button>
      </Group>
    </Modal>
  );
};
